/** @jsx jsx */
import React, { Fragment } from 'react'
import { jsx, Box, Flex } from 'theme-ui'
import styled from '@emotion/styled'
import Heading from '../shared/Typography/Heading/Heading'
import Paragraph from '../shared/Typography/Paragraph/Paragraph'

import { HeroImage, HeroImageAspectOvr } from '../shared/BgImage'
import Link from '../shared/Typography/Link/Link'
import theme from '../../gatsby-plugin-theme-ui'
import { useWindowSize } from '../../hooks/useWindowSize'
import Body from '../shared/Typography/Body/Body'
import Button from '../shared/Buttons/Button'
import Container from '../Container'
import Img from 'gatsby-image'

import { Youtube } from '../Youtube'

const BottomLeftTextContainer = styled.div`
  position: absolute;
  bottom: 1.5em;
  left: 1em;
  z-index: 10;
`

const ResponsiveImg = ({ mobileImage, desktopImage }) => {
  const { mobile } = useWindowSize()
  if (desktopImage.fluid) {
    const sources = mobileImage.fluid
      ? [
          mobileImage.fluid,
          {
            ...desktopImage.fluid,
            media: `(min-width: ${theme.responsive.small}px)`,
          },
        ]
      : desktopImage && desktopImage.fluid

    return <Img alt="Jaxxon Jewelry" {...props} fluid={sources} />
  }
  return (
    <img
      sx={{
        width: '100%',
        height: '100%',
        maxWidth: ['300px', '300px', '400px'],
      }}
      src={
        (mobile && mobileImage && mobileImage.file.url) || desktopImage.file.url
      }
    />
  )
}

const HeroGeneric = ({ flexProps, buttonProps, ...props }) => {
  return (
    <Flex
      sx={{
        ...flexProps,
      }}
    >
      {props.centeredHeaderImage && (
        <ResponsiveImg
          sx={{
            width: '100%',
            height: '100%',
            maxWidth: ['300px', '300px', '400px'],
          }}
          desktopImage={props.centeredHeaderImage}
          mobileImage={props.centeredHeaderImageMobile}
        />
      )}

      {props.title && (
        <Heading
          as="h1"
          sx={{
            color: props.textColor || 'white',
            fontSize: [5, 5, 5, 6, 8],
            lineHeight: [4, 4, 4, 5, 5],
          }}
          variant="semiBold"
        >
          {props.title}
        </Heading>
      )}
      {props.subTitle && (
        <Body
          variant="body1"
          sx={{
            fontWeight: 300,
            color: props.textColor || 'white',
            mb: [10],
            mt: [0, 0, 0, 10],
          }}
        >
          {props.subTitle}
        </Body>
      )}
      {props.actionButton && (
        <Button
          to={props.actionButton.toSlug}
          sx={{ mt: ['5px', '5px', 10, 10, 25] }}
          variant={props.actionButton.buttonStyle}
          size={props.actionButton.buttonSize || 'large'}
        >
          {props.actionButton.buttonLabel}
        </Button>
      )}
    </Flex>
  )
}

const HeroWrapperWithMobile = ({ desktopFlexProps, ...props }) => {
  return [
    <HeroGeneric
      {...props}
      flexProps={{
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        position: 'absolute',
        ...desktopFlexProps,
        display: ['none', null, null, null, 'flex'],
      }}
    />,
    <HeroGeneric
      {...props}
      flexProps={{
        display: ['flex', null, null, null, 'none'],
        zIndex: 10,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        position: 'absolute',
        right: 0,
        left: 0,
        bottom: '2.2em',
      }}
    />,
  ]
}

const generateVariations = (variant, props) => {
  if (variant === 'imageOnly') {
    return <Fragment />
  }

  if (variant === 'centeredContent') {
    return (
      <HeroWrapperWithMobile
        {...props}
        desktopFlexProps={{
          zIndex: 10,
        }}
      />
    )
  }
  if (variant === 'bottomLeftBoxText') {
    return (
      <BottomLeftTextContainer>
        <Box
          sx={{
            border: '2px solid #ffffff',
            padding: !props.inline
              ? '1.2em'
              : ['0px 20px 30px 20px', null, null, 32],
          }}
        >
          <Heading
            as="h5"
            variant="h5Bold"
            sx={{
              color: 'background',
              textAlign: 'center',
            }}
          >
            {props.title}
          </Heading>
        </Box>
      </BottomLeftTextContainer>
    )
  }

  if (variant === 'centerLeftAlign') {
    return (
      <HeroWrapperWithMobile
        {...props}
        desktopFlexProps={{
          bottom: '40%',
          left: props.inline ? 32 : '4em',
          alignItems: 'flex-start',
          textAlign: 'left',
        }}
      />
    )
  }

  if (variant === 'centerRightAlign') {
    return (
      <HeroWrapperWithMobile
        {...props}
        desktopFlexProps={{
          right: props.inline ? 32 : '4em',
          bottom: '40%',
          alignItems: 'flex-end',
          textAlign: 'right',
        }}
      />
    )
  }

  if (variant === 'lowerLeftAlign') {
    return (
      <HeroWrapperWithMobile
        {...props}
        desktopFlexProps={{
          left: props.inline ? 32 : '4em',
          bottom: props.inline ? 32 : '4em',
          alignItems: 'flex-start',
          textAlign: 'left',
        }}
      />
    )
  }

  if (variant === 'lowerRightAlign') {
    return (
      <HeroWrapperWithMobile
        {...props}
        desktopFlexProps={{
          right: props.inline ? 32 : '4em',
          bottom: props.inline ? 32 : '4em',
          alignItems: 'flex-end',
          textAlign: 'right',
        }}
      />
    )
  }

  if (variant === 'lowerCenterAlign') {
    return (
      <HeroWrapperWithMobile
        {...props}
        desktopFlexProps={{
          right: 0,
          left: 0,
          bottom: '4em',
        }}
      />
    )
  }
  return (
    <BottomLeftTextContainer>
      <Heading
        as="h5"
        variant="h5Bold"
        sx={{
          color: 'background',
          marginBottom: '8px',
        }}
      >
        {props.title}
      </Heading>
      {props.actionButton && (
        <Link to={props.actionButton.toSlug}>
          <Paragraph
            variant="textLinkSmall"
            sx={{
              color: 'background',
            }}
          >
            {props.actionButton.buttonLabel}
          </Paragraph>
        </Link>
      )}
    </BottomLeftTextContainer>
  )
}

const Hero = ({ ...props }) => {
  const { mobile } = useWindowSize()
  // art direction bug: https://github.com/gatsbyjs/gatsby/issues/24748  hack to fix for now
  const isCentered = props.layoutVariation === 'centeredContent'
  const sources = props.mobileImage
    ? [
        props.mobileImage.fluid,
        {
          ...props.image.fluid,
          media: `(min-width: ${theme.responsive.small}px)`,
        },
      ]
    : props.image && props.image.fluid
  const shouldShowVideo =
    (!mobile || (mobile && !props.mobileImage)) &&
    Boolean(props.backgroundVideoUrl)

  return (
    <Container
      fullWidth={!props.inline}
      sx={{
        width: 'auto',
      }}
      {...props}
    >
      <Box
        sx={{
          display: isCentered && 'flex',
          justifyContent: isCentered && 'center',
          alignItems: isCentered && 'center',
          width: '100%',
          position: 'relative',
          px: shouldShowVideo && props.inline && [0, null, null, null, '4rem'],
          height: props.inline && !props.backgroundVideoUrl && '70vh',
          ...props.videoContainterStyle,
        }}
      >
        {shouldShowVideo && (
          <Box
            sx={{
              position: 'relative',
            }}
          >
            <Box>
              {props.backgroundVideoUrl.includes('yout') ? (
                <Box
                  sx={{
                    overflow: 'hidden',
                    paddingTop: '56.25%',
                    position: 'relative',
                  }}
                >
                  <Youtube
                    videoURL={
                      mobile && props.mobileBackgroundVideoUrl
                        ? props.mobileBackgroundVideoUrl
                        : props.backgroundVideoUrl
                    }
                  />
                </Box>
              ) : (
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  sx={{
                    width: ['100%', '100%'],
                  }}
                  preload="auto"
                >
                  <source
                    src={
                      mobile && props.mobileBackgroundVideoUrl
                        ? props.mobileBackgroundVideoUrl
                        : props.backgroundVideoUrl
                    }
                    type="video/mp4"
                  />
                </video>
              )}
            </Box>
          </Box>
        )}
        {!shouldShowVideo &&
          (sources && sources.length ? (
            <HeroImageAspectOvr
              imgStyle={{
                objectFit: 'contain',
                width: '100%',
                height: 'auto',
              }}
              loading="eager"
              fadeIn={false}
              sources={sources}
              fluid={sources}
              backgroundColor={'#eeeeee'}
            />
          ) : (
            <HeroImage
              imgStyle={{
                objectFit: props.inline ? 'cover' : 'contain',
                width: '100%',
                height: props.inline ? '100%' : 'auto',
              }}
              loading="eager"
              fadeIn={false}
              fluid={sources}
              backgroundColor={'#eeeeee'}
            />
          ))}

        {generateVariations(props.layoutVariation, props)}
      </Box>
    </Container>
  )
}

export default Hero
